const SAFE_MESSAGES: Record<string, string> = {
  'invalid credentials': 'Invalid email or password.',
  'user not found': "We couldn't find an account with that email.",
  'email already exists': 'An account with this email already exists.',
  'email already in use': 'An account with this email already exists.',
  'session not found': 'Session not found. Please start a new one.',
  'simulation not found': 'Simulation not found.',
  unauthorized: 'Please log in to continue.',
  'token expired': 'Your session has expired. Please log in again.',
  'too many requests': "You're making requests too quickly. Please wait a moment.",
  'rate limit exceeded': "You're making requests too quickly. Please wait a moment.",
  'missing required fields': 'Please fill in all required fields.',
  'invalid email': 'Please enter a valid email address.',
  'password too short': 'Password must be at least 8 characters.',
  'network error': 'Connection issue. Please check your internet and try again.',
  'failed to fetch': 'Connection issue. Please check your internet and try again.',
};

const TECHNICAL_PATTERNS = [
  /api.key/i,
  /status.code/i,
  /websocket/i,
  /econnrefused/i,
  /enotfound/i,
  /timeout.*exceeded/i,
  /internal.server.error/i,
  /dynamodb/i,
  /openai/i,
  /elevenlabs/i,
  /anam/i,
  /convai/i,
  /permission/i,
  /cors/i,
  /request failed with status/i,
  /unexpected token/i,
  /syntax.error/i,
  /cannot read propert/i,
  /undefined is not/i,
  /null is not/i,
  /chunk/i,
  /module/i,
  /stack/i,
];

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

export function friendlyError(error: unknown, fallback?: string): string {
  const raw = extractMessage(error);
  if (!raw) return fallback || DEFAULT_MESSAGE;

  const lower = raw.toLowerCase().trim();

  for (const [key, friendly] of Object.entries(SAFE_MESSAGES)) {
    if (lower.includes(key)) return friendly;
  }

  for (const pattern of TECHNICAL_PATTERNS) {
    if (pattern.test(raw)) return fallback || DEFAULT_MESSAGE;
  }

  if (raw.length > 120) return fallback || DEFAULT_MESSAGE;

  return raw;
}

/**
 * The chat routes answer the superadmin testing models in Settings -> Model lab
 * with a diagnostic body, `{ message: 'Chat failed: <reason>', model }`, and
 * the `model` field is the marker: nobody else ever receives it. For that body
 * the reason is the whole point (which parameter the model refused, say), so
 * it is shown verbatim. Passing it through friendlyError would mask it, since
 * the filter drops anything over 120 characters or mentioning OpenAI, and the
 * tester would see the generic fallback exactly as before. Every other error
 * keeps the friendly treatment.
 */
export function friendlyErrorWithReason(error: unknown, fallback?: string): string {
  const data = (error as any)?.response?.data;
  if (data && typeof data === 'object' && typeof data.model === 'string' && data.model) {
    const reason = String(data.message || '').trim();
    if (reason) return reason;
  }
  return friendlyError(error, fallback);
}

function extractMessage(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error.trim();
  const e = error as any;
  return String(e?.response?.data?.message || e?.message || e?.error || '').trim();
}
